import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FiGrid, FiCheck, FiClock, FiX } from "react-icons/fi";
import { fetchbusinessCredentials } from "../../redux/slices/businessSlice";
import { postRequestAuthenticated } from "../../services/rest";
import DomainStep from "./steps/DomainStep";
import CustomizeStep from "./steps/CustomizeStep";
import BuildCommunityStep from "./steps/BuildCommunityStep";
import EmbedInstaller from "../EmbedInstaller/EmbedInstaller";
import UpgradeBanner, { hasGrowth } from "../Admin/widgets/UpgradeBanner";

const STEPS = [
  { key: "account", n: "01", label: "Account" },
  { key: "domain", n: "02", label: "Verify domain" },
  { key: "community", n: "03", label: "Build community" },
  { key: "embed", n: "04", label: "Embed install" },
  { key: "customize", n: "05", label: "Customize" },
];

// Map onboardingState → furthest reached step index. Flow: verify domain first,
// then build community.
const stateToStep = (s) => {
  switch (s) {
    case "signup":
    case "crawl_done":
    case "domain_pending":
      return 1; // verify domain
    case "domain_verified":
    case "draft_ready":
      return 2; // build community
    case "community_committed":
      return 3; // embed
    case "embed_live":
      return 4; // customize
    case "complete":
      return 5;
    default:
      return 1;
  }
};

const ComingSoon = ({ title, note }) => (
  <div className="mx-auto flex max-w-xl flex-col items-center justify-center gap-3 py-16 text-center">
    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-theme-tertiaryBackground">
      <FiClock size={22} className="text-theme-accent" />
    </div>
    <p className="text-lg font-semibold text-theme-secondaryText">{title}</p>
    <p className="max-w-sm text-sm text-theme-placeholder">{note}</p>
  </div>
);

const StatusPill = ({ business }) => {
  // Don't show "Live" until the domain is verified — note we do NOT key off
  // business.isVerified (true for any paid business, not a domain signal).
  const complete = ["domain_verified", "embed_live", "complete"].includes(
    business?.onboardingState
  );
  const label = complete ? "Live" : "Setup in progress";
  const tone = complete
    ? "text-theme-success border-theme-success"
    : "text-theme-warning border-theme-warning";
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${tone}`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {label}
    </span>
  );
};

const SetupWizard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const business = useSelector((state) => state.business.business);
  const [current, setCurrent] = useState(1);

  useEffect(() => {
    dispatch(fetchbusinessCredentials());
  }, [dispatch]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (!isLoggedIn) navigate(`/get-started?redirect=/admin/setup`);
    }, 400);
    return () => clearTimeout(t);
  }, [isLoggedIn, navigate]);

  // Subscription-gate the wizard: onboarding (incl. domain verification) must
  // happen AFTER subscribing. If the business has loaded with no active
  // subscription, send them to the dashboard (which shows Billing only).
  const businessLoaded = business && Object.keys(business).length > 0;
  useEffect(() => {
    if (businessLoaded && !business?.current_subscription) {
      navigate("/admin/dashboard");
    }
  }, [businessLoaded, business, navigate]);

  // Note: the wizard is now intentionally re-accessible after "complete" (via
  // the "Channels AI" sidebar item), so we no longer bounce completed users to
  // the dashboard.

  const reached = stateToStep(business?.onboardingState);
  useEffect(() => {
    // On a completed community, re-entry via "Channels AI" should land on the
    // AI "Build community" step (drafting), not the last step. Otherwise open
    // the furthest reached step, clamped to a valid index.
    if (business?.onboardingState === "complete") {
      const communityStep = STEPS.findIndex((s) => s.key === "community");
      setCurrent(communityStep >= 0 ? communityStep : 2);
      return;
    }
    setCurrent((c) => Math.min(STEPS.length - 1, Math.max(c, reached)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reached, business?.onboardingState]);

  const goNext = () => setCurrent((c) => Math.min(STEPS.length - 1, c + 1));
  const goTo = (i) => i <= reached && setCurrent(i);

  const doneCount = STEPS.filter((_, i) => i < reached).length;
  const pct = Math.round((doneCount / STEPS.length) * 100);

  const renderStep = () => {
    switch (STEPS[current].key) {
      case "account":
        return (
          <div className="mx-auto max-w-xl py-10 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-theme-success text-white">
              <FiCheck size={22} />
            </div>
            <h2 className="text-xl font-semibold text-theme-secondaryText">
              Account created
            </h2>
            <p className="mt-1 text-sm text-theme-placeholder">
              Let's set up your community.
            </p>
            <button
              onClick={goNext}
              className="mt-6 rounded-lg bg-theme-accent px-5 py-2.5 text-sm font-semibold text-theme-accentText hover:bg-theme-accentHover"
            >
              Get started →
            </button>
          </div>
        );
      case "community":
        return <BuildCommunityStep onDone={goNext} />;
      case "domain":
        return <DomainStep onVerified={() => dispatch(fetchbusinessCredentials())} />;
      case "embed":
        return (
          <div className="mx-auto max-w-2xl">
            <div className="mb-5">
              <h2 className="text-xl font-semibold text-theme-secondaryText">
                Install the embed
              </h2>
              <p className="mt-1 text-sm text-theme-placeholder">
                Choose where your community lives and copy the snippet into your
                site or app.
              </p>
            </div>
            <EmbedInstaller apiKey={business?.apiKey} />
            <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-theme-placeholder">
                Not ready to install it? You can grab the snippet anytime later
                from the Integration tab — continue for now.
              </p>
              <button
                onClick={async () => {
                  // Mark the embed step done (embed_live) so the stepper checks
                  // it off, then move on.
                  try {
                    await postRequestAuthenticated("/setup/embed-installed", {});
                    dispatch(fetchbusinessCredentials());
                  } catch (e) {}
                  goNext();
                }}
                className="rounded-lg bg-theme-accent px-5 py-2.5 text-sm font-semibold text-theme-accentText hover:bg-theme-accentHover"
              >
                Continue →
              </button>
            </div>
          </div>
        );
      case "customize":
        return <CustomizeStep onDone={() => navigate("/admin/dashboard")} />;
      default:
        return null;
    }
  };

  // Channels AI is a full-page Growth feature on RE-ACCESS. First-time setup
  // (onboarding not yet "complete") is open to all plans (one-time use); once
  // complete, a non-Growth business only sees the upgrade banner — no wizard.
  const growthGated =
    businessLoaded &&
    !hasGrowth(business) &&
    business?.onboardingState === "complete";

  if (growthGated) {
    return (
      <div className="flex h-screen w-full flex-col bg-theme-primaryBackground">
        <header className="flex flex-shrink-0 items-center justify-between border-b border-theme-chatDivider px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-theme-accent">
              <FiGrid size={16} className="text-theme-accentText" />
            </div>
            <span className="text-sm font-bold text-theme-secondaryText">
              Channels AI
            </span>
          </div>
          <button
            onClick={() => navigate("/admin/dashboard")}
            className="flex items-center gap-1.5 text-sm text-theme-placeholder hover:text-theme-secondaryText"
          >
            <FiX size={16} /> Exit
          </button>
        </header>
        <div className="flex flex-1 items-center justify-center p-6">
          <div className="w-full max-w-md space-y-3 text-center">
            <UpgradeBanner feature="Channels AI" />
            <p className="text-sm text-theme-placeholder">
              Your community is already set up. Re-running the AI setup assistant
              is a Growth feature — upgrade to use it anytime.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen w-full flex-col bg-theme-primaryBackground">
      {/* Top bar with Setup-in-progress status */}
      <header className="flex flex-shrink-0 items-center justify-between border-b border-theme-chatDivider px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-theme-accent">
            <FiGrid size={16} className="text-theme-accentText" />
          </div>
          <span className="text-sm font-bold text-theme-secondaryText">
            Channels setup
          </span>
        </div>
        <div className="flex items-center gap-3">
          <StatusPill business={business} />
          <button
            onClick={() => navigate("/admin/dashboard")}
            className="flex items-center gap-1.5 text-sm text-theme-placeholder hover:text-theme-secondaryText"
          >
            <FiX size={16} /> Exit
          </button>
        </div>
      </header>

      {/* Mobile / tablet: horizontal stepper */}
      <div className="flex flex-shrink-0 items-center gap-1 overflow-x-auto border-b border-theme-chatDivider px-3 py-2.5 md:hidden">
        {STEPS.map((s, i) => {
          const done = i < reached;
          const active = i === current;
          return (
            <button
              key={s.key}
              onClick={() => goTo(i)}
              disabled={i > reached}
              className={`flex items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 py-1.5 text-xs font-semibold ${
                active
                  ? "bg-theme-accentSoft text-theme-accent"
                  : done
                  ? "text-theme-success"
                  : "text-theme-placeholder"
              } ${i > reached ? "opacity-50" : ""}`}
            >
              <span
                className={`flex h-5 w-5 items-center justify-center rounded-full border text-[10px] ${
                  done
                    ? "border-theme-success bg-theme-success text-white"
                    : active
                    ? "border-theme-accent"
                    : "border-theme-chatDivider"
                }`}
              >
                {done ? <FiCheck size={11} /> : s.n}
              </span>
              {s.label}
            </button>
          );
        })}
      </div>

      <div className="flex min-h-0 flex-1">
        {/* Desktop: left SETUP PROGRESS sidebar */}
        <aside className="hidden w-72 flex-shrink-0 flex-col border-r border-theme-chatDivider bg-theme-sidebarBackground px-5 py-6 md:flex">
          <p className="mb-5 text-xs font-bold uppercase tracking-wider text-theme-placeholder">
            Setup progress
          </p>
          <div className="flex flex-1 flex-col">
            {STEPS.map((s, i) => {
              const done = i < reached;
              const active = i === current;
              const last = i === STEPS.length - 1;
              return (
                <button
                  key={s.key}
                  onClick={() => goTo(i)}
                  disabled={i > reached}
                  className={`group flex items-start gap-3 text-left ${
                    i > reached ? "cursor-not-allowed" : ""
                  }`}
                >
                  <div className="flex flex-col items-center">
                    <span
                      className={`flex h-7 w-7 items-center justify-center rounded-full border text-xs font-semibold transition-colors ${
                        done
                          ? "border-theme-success bg-theme-success text-white"
                          : active
                          ? "border-theme-accent text-theme-accent"
                          : "border-theme-chatDivider text-theme-placeholder"
                      }`}
                    >
                      {done ? <FiCheck size={13} /> : s.n}
                    </span>
                    {!last && (
                      <span
                        className={`my-1 w-0.5 flex-1 ${
                          done ? "bg-theme-success" : "bg-theme-chatDivider"
                        }`}
                        style={{ minHeight: 22 }}
                      />
                    )}
                  </div>
                  <span
                    className={`pt-1 text-sm ${
                      active
                        ? "font-semibold text-theme-secondaryText"
                        : done
                        ? "text-theme-placeholder"
                        : "text-theme-primaryText"
                    }`}
                  >
                    {s.label}
                  </span>
                </button>
              );
            })}
          </div>
          {/* progress bar */}
          <div className="mt-4">
            <div className="mb-2 h-1.5 w-full overflow-hidden rounded-full bg-theme-tertiaryBackground">
              <div
                className="h-full rounded-full bg-theme-accent transition-all duration-500"
                style={{ width: `${pct}%` }}
              />
            </div>
            <p className="text-xs text-theme-placeholder">
              {doneCount} of {STEPS.length} steps done
            </p>
          </div>
        </aside>

        {/* Step content */}
        <main className="min-h-0 flex-1 overflow-y-auto px-6 py-8 custom-scrollbar">
          {renderStep()}
        </main>
      </div>
    </div>
  );
};

export default SetupWizard;
